// @ts-nocheck

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader } from '@components/card/card.jsx';
import { AppSettings } from '@config/app-settings.js';
import PerfectScrollbar from 'react-perfect-scrollbar';
import AddExtraRecord from '../Add/AddExtraRecord.jsx';
import UpdateExtraRecord from '../Update/UpdateExtraRecord.jsx';
import { useSelector } from 'react-redux';
import Loader from '@components/Shared/Loader/Loader.jsx';
import useAddUpdateExtraInformation from '@pages/ExtraInformation/ExtraInformationBuilder/useAddUpdateExtraInformation.js';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import showConfirmation from '@components/Shared/Alert/Confirmation.js';
import { ntxAPI } from '@API/axiosNeatex.js';

// --- مكون فرعي لعنصر الشجرة للتحكم في الفتح والاغلاق بشكل نظيف ---
const TreeItem = ({ tree, i18n, activeId, onNodeClick, onAddClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = tree?.recordValues?.length > 0;

  const toggleExpand = e => {
    e.preventDefault();
    setIsExpanded(!isExpanded);
  };

  return (
    <div className='tree-wrapper mb-2'>
      <div
        className={`d-flex justify-content-between border border-1 align-items-center rounded-2 p-1 transition-all ${
          isExpanded ? 'bg-secondary bg-opacity-60' : 'bg-white1 hover-bg-light'
        }`}
        // style={{ border: '1px solid #eee' }}
      >
        {/* File Node Parent */}
        <div className='d-flex align-items-center flex-grow-1 cursor-pointer' onClick={toggleExpand}>
          <span className='me-2 text-muted'>
            {/* تبديل الأيقونة بناء على الحالة */}
            <i className={`fa ${isExpanded ? 'fa-folder-open text-warning' : 'fa-folder text-warning'} fa-lg`}></i>
          </span>
          <span className='fw-bold text-body'>{i18n.language === 'en' ? tree.name : tree.name2}</span>
          {hasChildren && (
            <span className='ms-auto text-muted small'>
              <i className={`fa fa-chevron-${isExpanded ? 'down' : i18n.language === 'ar' ? 'left' : 'right'}`}></i>
            </span>
          )}
        </div>

        {/* Add Button */}
        <button
          className='btn btn-sm btn-outline-theme ms-2 border-0'
          onClick={e => {
            e.stopPropagation();
            onAddClick(tree);
          }}
          title='Add New Record'
        >
          <i className='fa fa-plus'></i>
        </button>
      </div>

      {/* Children Nodes with Animation Logic could be added here, simple condition for now */}
      {isExpanded && hasChildren && (
        <div className='ps-4 pt-1 border-start ms-2 my-1'>
          {tree.recordValues.map((node, idx) => {
            const isActive = activeId === node.extraInformationId;
            return (
              <div
                key={node.extraInformationId}
                className={`d-flex align-items-center p-2 rounded cursor-pointer mb-1 ${
                  isActive ? 'bg-theme text-white shadow-sm' : 'text-body hover-bg-gray-100'
                }`}
                onClick={() => onNodeClick(tree, node)}
              >
                <i className={`${tree?.icon || 'fa fa-file-alt'} me-2 ${isActive ? 'text-white' : 'text-muted'}`}></i>
                <span className='text-truncate'>
                  {(i18n.language === 'en' ? node.value : node.value2) || `Record #${idx + 1}`}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {isExpanded && !hasChildren && (
        <div className='ps-4 py-2 text-muted small fst-italic'>
          {i18n.language === 'en' ? 'No records found' : 'لا توجد سجلات'}
        </div>
      )}
    </div>
  );
};

const ExtraRecords = ({ FormRecordWithValueList, ReferenceId, setRefreshData }) => {
  const { t, i18n } = useTranslation();
  const Navigate = useNavigate();
  const context = useContext(AppSettings);

  // State
  const [extraInformationId, setExtraInformationId] = useState(null);
  const [formId, setFormId] = useState(null);
  const [formName, setFormName] = useState('');
  const [isAdd, setIsAdd] = useState(false);
  const [extraInfoFormData, setExtraInfoFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Redux
  const { loadingRecordWithValueList } = useSelector(state => state.ExtraInformation);

  // Hooks
  const { AddUpdateExtraInformation } = useAddUpdateExtraInformation();
  const sidebarRef = useRef(null);
  const [isSidebarToggled, setIsSidebarToggled] = useState(false);

  // Clean up layout on unmount
  useEffect(() => {
    context.setAppContentFullHeight(true);
    return () => context.setAppContentFullHeight(false);
  }, []);

  // --- Handlers ---

  const handleAddUpdateExtraInfo = async () => {
    setIsSubmitting(true);
    try {
      const response = await AddUpdateExtraInformation(extraInfoFormData);
      if (!response) throw new Error('Save failed');

      CustomAlert({
        action: extraInformationId ? 'Edit' : 'Add',
        msg: extraInformationId ? t('Data updated successfully') : t('Record has been added successfully')
      });

      setRefreshData(true);
      if (!extraInformationId) handleResetView();
    } catch (err) {
      CustomAlert({ action: 'Error', msg: t('Error occurred while saving') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRecord = async () => {
    showConfirmation(
      i18n.language === 'en' ? 'Are you sure you want to delete this record?' : 'هل أنت متأكد من حذف هذا السجل؟',
      async () => {
        try {
          setIsDeleting(true);
          const { data } = await ntxAPI.post(
            `ExtraInformation/DeleteExtraInformation?ExtraInformationId=${extraInformationId}`
          );
          if (data?.succeeded) {
            CustomAlert({ action: 'Delete', msg: t('Record deleted successfully') });
            handleResetView();
            setRefreshData(true);
          } else {
            CustomAlert({ action: 'Error' });
          }
        } catch {
          CustomAlert({ action: 'Error' });
        } finally {
          setIsDeleting(false);
        }
      }
    );
  };

  const handleNodeClick = (tree, node) => {
    setExtraInformationId(node.extraInformationId);
    setFormName(i18n.language === 'en' ? tree?.name : tree?.name2);
    setIsAdd(false);
    // Close sidebar on mobile after selection
    if (window.innerWidth < 768) setIsSidebarToggled(false);
  };

  const handleAddClick = tree => {
    setFormId(tree?.id);
    setFormName(i18n.language === 'en' ? tree?.name : tree?.name2);
    setIsAdd(true);
    setExtraInformationId(null);
    if (window.innerWidth < 768) setIsSidebarToggled(false);
  };

  const handleResetView = () => {
    setExtraInformationId(null);
    setIsAdd(false);
    setFormId(null);
    setFormName('');
  };

  // Mobile Sidebar Logic
  const toggleSidebar = () => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const isRTL = i18n.dir() === 'rtl';

    if (isSidebarToggled) {
      sidebar.style[isRTL ? 'right' : 'left'] = ''; // Reset to CSS default (hidden)
    } else {
      sidebar.style[isRTL ? 'right' : 'left'] = '0px'; // Show
    }
    setIsSidebarToggled(!isSidebarToggled);
  };

  return (
    <div className='d-flex flex-column h-100 position-relative p-0'  >
      <Loader loading={loadingRecordWithValueList} />

      <Card className='flex-1 m-0 d-flex flex-column overflow-hidden border-0 shadow-sm'>
        {/* Header Section */}
        <CardHeader className='fw-bold d-flex bg-white1 border-bottom align-items-center py-3'>
          <div className='d-flex align-items-center flex-grow-1'>
            {/* Mobile Toggle */}
            <button className='btn btn-sm btn-light border me-2 d-md-none' onClick={toggleSidebar}>
              <i className='fa fa-bars'></i>
            </button>
            <div className='text-truncate'>
              <span className='text-muted me-2'>{t('ExtraRecord')}</span>
              {formName && (
                <>
                  <i className={`fa fa-chevron-${i18n.dir() === 'rtl' ? 'left' : 'right'} fa-xs text-muted mx-1`}></i>
                  <span className='text-theme'>{formName}</span>
                </>
              )}
            </div>
          </div>

          {/* Actions Toolbar */}
          <div className='d-flex gap-2'>
            {(isAdd || extraInformationId) && (
              <>
                <button
                  type='submit'
                  form='extra-info-form'
                  className='btn btn-sm btn-primary px-3 d-flex align-items-center gap-2'
                  disabled={isSubmitting || isDeleting}
                >
                  {isSubmitting ? (
                    <div className='spinner-border spinner-border-sm'></div>
                  ) : (
                    <i className={`fa fa-${extraInformationId ? 'save' : 'plus'}`}></i>
                  )}
                  <span className='d-none d-sm-inline'>
                    {extraInformationId
                      ? i18n.language === 'en'
                        ? 'Save Changes'
                        : 'حفظ التغييرات'
                      : i18n.language === 'en'
                      ? 'Add Record'
                      : 'إضافة سجل'}
                  </span>
                </button>

                {extraInformationId && (
                  <button
                    className='btn btn-sm btn-outline-danger'
                    disabled={isSubmitting || isDeleting}
                    onClick={handleDeleteRecord}
                    title={t('Delete')}
                  >
                    {isDeleting ? (
                      <div className='spinner-border spinner-border-sm'></div>
                    ) : (
                      <i className='fas fa-trash-alt'></i>
                    )}
                  </button>
                )}
              </>
            )}

            {(isAdd || extraInformationId) && (
              <button className='btn btn-sm btn-light border' onClick={handleResetView} title={t('Close')}>
                <i className='fa fa-times'></i>
              </button>
            )}
          </div>
        </CardHeader>

        <CardBody className='p-0 flex-1 overflow-hidden d-flex bg-light1'>
          {/* --- Sidebar --- */}
          <div
            className='file-manager-sidebar bg-white1 border-end h-100 shadow-sm bg-body '
            ref={sidebarRef}
            style={{ zIndex: 10 }}
          >
            <div className='p-2 border  '>
              <small className='text-muted text-uppercase fw-bold ls-1'>
                {i18n.language === 'en' ? 'Forms List' : 'قائمة النماذج'}
              </small>
            </div>
            <PerfectScrollbar className='h-100 p-3'>
              {FormRecordWithValueList?.map(tree => (
                <TreeItem
                  key={tree.id}
                  tree={tree}
                  i18n={i18n}
                  activeId={extraInformationId}
                  onNodeClick={handleNodeClick}
                  onAddClick={handleAddClick}
                />
              ))}
              {(!FormRecordWithValueList || FormRecordWithValueList.length === 0) && !loadingRecordWithValueList && (
                <div className='text-center text-muted mt-5'>
                  <i className='fa fa-inbox fa-2x mb-2 opacity-50'></i>
                  <p>{t('No forms available')}</p>
                </div>
              )}
            </PerfectScrollbar>
          </div>

          {/* --- Overlay for mobile when sidebar is open --- */}
          {isSidebarToggled && (
            <div
              className='position-absolute top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-md-none'
              style={{ zIndex: 5 }}
              onClick={toggleSidebar}
            ></div>
          )}

          {/* --- Main Content Area --- */}
          <div className='flex-1 d-flex flex-column h-100 bg-white1 position-relative'>
            <PerfectScrollbar className='h-100'>
              <form
                id='extra-info-form'
                className='h-100'
                onSubmit={e => {
                  e.preventDefault();
                  handleAddUpdateExtraInfo();
                }}
              >
                <div className='p-4 container-fluid'>
                  {/* Add Mode */}
                  {isAdd && formId && (
                    <div className='animate__animated animate__fadeIn'>
                      <div className='d-flex align-items-center mb-4 pb-2 border-bottom'>
                        <div className='bg-primary bg-opacity-10 p-2 rounded me-3 text-primary'>
                          <i className='fa fa-plus-circle fa-lg'></i>
                        </div>
                        <div>
                          <h5 className='mb-0'>{i18n.language === 'en' ? 'New Record' : 'سجل جديد'}</h5>
                          <small className='text-muted'>
                            {i18n.language === 'en' ? 'Fill in the information below' : 'املأ المعلومات أدناه'}
                          </small>
                        </div>
                      </div>
                      <AddExtraRecord
                        FormId={formId}
                        ReferenceId={ReferenceId}
                        setExtraInfoFormData={setExtraInfoFormData}
                      />
                    </div>
                  )}

                  {/* Edit Mode */}
                  {!isAdd && extraInformationId && (
                    <div className='animate__animated animate__fadeIn'>
                      <div className='d-flex align-items-center mb-4 pb-2 border-bottom'>
                        <div className='bg-warning bg-opacity-10 p-2 rounded me-3 text-warning'>
                          <i className='fa fa-edit fa-lg'></i>
                        </div>
                        <div>
                          <h5 className='mb-0'>{i18n.language === 'en' ? 'Edit Record' : 'تعديل السجل'}</h5>
                          <small className='text-muted'>
                            {i18n.language === 'en' ? 'Update the information below' : 'حدّث المعلومات أدناه'}
                          </small>
                        </div>
                      </div>
                      <UpdateExtraRecord
                        extraInformationId={extraInformationId}
                        ReferenceId={ReferenceId}
                        setExtraInfoFormData={setExtraInfoFormData}
                      />
                    </div>
                  )}

                  {/* Empty State */}
                  {!isAdd && !extraInformationId && (
                    <div className='d-flex flex-column justify-content-center align-items-center h-100 text-muted py-5 opacity-75'>
                      <div className=' p-4 mb-3'>
                        <i className='fa fa-hand-pointer fa-3x text-theme '></i>
                      </div>
                      <h5 className='fw-normal'>{i18n.language === 'en' ? 'No Selection' : 'لا يوجد تحديد'}</h5>
                      <p className='small mb-0 text-center' style={{ maxWidth: '250px' }}>
                        {i18n.language === 'en'
                          ? 'Please select a record from the list to view details or click + to add new.'
                          : 'يرجى تحديد سجل من القائمة لعرض التفاصيل أو النقر على + لإضافة جديد.'}
                      </p>
                    </div>
                  )}
                </div>
              </form>
            </PerfectScrollbar>
          </div>
        </CardBody>
      </Card>

      <style>{`
        .hover-bg-light:hover { background-color: rgba(var(--bs-theme-rgb), 0.9 ); }
        .hover-bg-gray-100:hover { background-color: rgba(var(--bs-theme-rgb), 0.9 ); }
        .cursor-pointer { cursor: pointer; }
 
        
        /* Custom scrollbar tweak if needed */
        .ps__rail-y { right: 2px !important; }
        
        /* Transitions */
        .transition-all { transition: all 0.2s ease; }
      `}</style>
    </div>
  );
};

export default ExtraRecords;

// const ExtraRecords = ({ FormRecordWithValueList, ReferenceId, setRefreshData }) => {
//   const { t, i18n } = useTranslation();
//   const [extraInformationId, setExtraInformationId] = useState(null);
//   const [formId, setFormId] = useState(null);
//   const [formName, setFormName] = useState('');
//   const Navigate = useNavigate();
//   const context = useContext(AppSettings);
//   const [isAdd, setIsAdd] = useState(false);
//   const { loadingRecordWithValueList } = useSelector(state => state.ExtraInformation);

//   const [extraInfoFormData, setExtraInfoFormData] = useState({}); // <-- هذي البيانات اللي تبنيها النماذج
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [isDeleting, setIsDeleting] = useState(false);

//   const { AddUpdateExtraInformation } = useAddUpdateExtraInformation();

//   // ----------------- عمليات الحفظ والحذف -------------------
//   const handleAddUpdateExtraInfo = async () => {
//     setIsSubmitting(true);
//     try {
//       const response = await AddUpdateExtraInformation(extraInfoFormData);
//       if (!response) {
//         CustomAlert({ action: 'Error', msg: t('Error') });
//         return;
//       }

//       CustomAlert({
//         action: extraInformationId ? 'Edit' : 'Add',
//         msg: extraInformationId ? t('Data updated successfully') : t('Record has been added successfully')
//       });

//       setRefreshData(true);
//       if (!extraInformationId) setExtraInformationId(null); // reset بعد الإضافة
//     } catch (err) {
//       CustomAlert({ action: 'Error' });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handleDeleteRecord = async () => {
//     showConfirmation(
//       i18n.language === 'en' ? 'Are you sure you want to delete this record?' : 'هل أنت متأكد من حذف هذا السجل؟',
//       async () => {
//         try {
//           debugger;
//           setIsDeleting(true);
//           const { data } = await ntxAPI.post(
//             `ExtraInformation/DeleteExtraInformation?ExtraInformationId=${extraInformationId}`
//           );
//           if (data?.succeeded) {
//             CustomAlert({ action: 'Delete', msg: t('Record deleted successfully') });
//             setExtraInformationId(null);
//             setRefreshData(true);
//           } else {
//             CustomAlert({ action: 'Error' });
//           }
//         } catch {
//           CustomAlert({ action: 'Error' });
//         } finally {
//           setIsDeleting(false);
//         }
//       }
//     );
//   };

//   // ---------------------------------------------------------

//   const handleclickNode = (tree, node) => {
//     setExtraInformationId(node.extraInformationId);
//     setFormName(i18n.language === 'en' ? tree?.name : tree?.name2);
//     setIsAdd(false);
//     handleSidebarMobile();
//   };
//   const handleclickTree = tree => {
//     setFormId(tree?.id);
//     setFormName(i18n.language === 'en' ? tree?.name : tree?.name2);
//     setIsAdd(true);
//     setExtraInformationId(null);
//   };
//   //_______________________________________________________________________________________

//   //#region file-node.has-sub
//   useEffect(() => {
//     var fileHasSubNodes = document.querySelectorAll('.file-node.has-sub');

//     fileHasSubNodes.forEach(node => {
//       var fileArrow = node.querySelector('.file-link > .file-arrow');

//       fileArrow.addEventListener('click', function (event) {
//         event.preventDefault();
//         node.classList.toggle('expand');
//       });
//     });

//     var fileInfoNodes = document.querySelectorAll('.file-node');

//     fileInfoNodes.forEach(node => {
//       var fileInfo = node.querySelector('.file-link > .file-info');

//       fileInfo.addEventListener('click', function (event) {
//         event.preventDefault();
//         fileInfoNodes.forEach(otherNode => {
//           if (otherNode !== node) {
//             otherNode.classList.remove('selected');
//           }
//         });
//         node.classList.add('expand');
//         node.classList.add('selected');
//       });
//     });

//     context.setAppContentFullHeight(true);

//     return function cleanUp() {
//       context.setAppContentFullHeight(false);
//     };

//     // eslint-disable-next-line
//   }, [FormRecordWithValueList]);

//   //#endregion

//   //#region //____ handle Sidebar Mobile _______
//   const sidebarRef = useRef(null);
//   const [isToggled, setIsToggled] = useState(false);
//   const handleSidebarMobile = () => {
//     const sidebarElement = sidebarRef.current;
//     if (localStorage.i18nextLng === 'en') {
//       sidebarElement.style.left = isToggled ? '0px' : '';
//       setIsToggled(!isToggled);
//     } else {
//       sidebarElement.style.right = isToggled ? '0px' : '';
//       setIsToggled(!isToggled);
//     }
//   };

//   //#endregion

//   return (
//     <>
//       <div className='d-flex flex-column' style={{ height: '90%' }}>
//         <Loader loading={loadingRecordWithValueList} />
//         <Card className='flex-1 m-0 d-flex flex-column overflow-hidden rounded-0'>
//           <CardHeader className='fw-bold d-flex bg-theme bg-opacity-25 align-items-center'>
//             <div className='flex-grow-1'>{t('ExtraRecord')}</div>

//             <div className='flex-grow-1 text-center'>
//               <h5 className='mb-0'>{formName}</h5>
//             </div>

//             <div className='d-flex flex-grow-1 justify-content-end gap-2'>
//               {(isAdd || extraInformationId) && (
//                 <>
//                   <button
//                     type='submit'
//                     form='extra-info-form' // ⬅️ يربط الزر بالنموذج
//                     className='btn btn-sm btn-theme'
//                     disabled={isSubmitting || isDeleting}
//                     // onClick={handleAddUpdateExtraInfo}
//                     title={extraInformationId ? t('Save') : t('Add')}
//                   >
//                     {isSubmitting ? (
//                       <div className='spinner-border spinner-border-sm'></div>
//                     ) : (
//                       <i className={`fa fa-${extraInformationId ? 'save' : 'plus'}`}></i>
//                     )}
//                   </button>

//                   {extraInformationId && (
//                     <button
//                       className='btn btn-sm btn-danger'
//                       disabled={isSubmitting || isDeleting}
//                       onClick={handleDeleteRecord}
//                       title={t('Delete Record')}
//                     >
//                       {isDeleting ? (
//                         <div className='spinner-border spinner-border-sm'></div>
//                       ) : (
//                         <i className='fas fa-trash-alt'></i>
//                       )}
//                     </button>
//                   )}
//                 </>
//               )}

//               <button
//                 className='btn btn-sm btn-secondary'
//                 onClick={() => {
//                   setExtraInformationId(null);
//                   setIsAdd(false);
//                   setFormId(null);
//                   setFormName('');
//                 }}
//                 title={t('Cancel')}
//               >
//                 <i className='fa fa-times'></i>
//               </button>
//             </div>
//           </CardHeader>

//           <CardBody className='p-0 flex-1 overflow-hidden'>
//             <div className='file-manager h-100'>
//               <div className='file-manager-container'>
//                 <div ref={sidebarRef} className='file-manager-sidebar'>
//                   <div className='file-manager-sidebar-mobile-toggler'>
//                     <button type='button' className='btn' onClick={handleSidebarMobile}>
//                       <i className='far fa-lg fa-folder'></i>
//                     </button>
//                   </div>
//                   <div className='file-manager-sidebar-content'>
//                     <PerfectScrollbar className='h-100 p-2'>
//                       <div className='file-tree mb-3'>
//                         {FormRecordWithValueList?.map((tree, idx) => (
//                           <Fragment key={tree.id}>
//                             <div className='tree-wrapper'>
//                               <div className='d-flex mb-2 justify-content-between bg-inverse bg-opacity-10 rounded-0'>
//                                 <div className='file-node has-sub '>
//                                   <Link to='' className='file-link'>
//                                     <span className='file-arrow'></span>
//                                     <span className='file-info'>
//                                       <span className='file-icon'>
//                                         <i className='fa fa-folder fa-lg text-warning'></i>
//                                       </span>
//                                       <span className='file-text'>
//                                         {i18n.language === 'en' ? tree.name : tree.name2}
//                                       </span>
//                                     </span>
//                                   </Link>

//                                   <div className='file-tree'>
//                                     {tree?.recordValues?.map((node, idx) => (
//                                       <div className='file-node' key={node?.extraInformationId}>
//                                         <Link to='' className='file-link'>
//                                           <span className='file-arrow'></span>
//                                           <span className='file-info' onClick={() => handleclickNode(tree, node)}>
//                                             <span className='file-icon'>
//                                               <i className={tree?.icon}></i>
//                                             </span>
//                                             <span className='file-text'>
//                                               {(i18n.language === 'en' ? node.value : node.value2) || ++idx}
//                                             </span>
//                                           </span>
//                                         </Link>
//                                       </div>
//                                     ))}
//                                   </div>
//                                 </div>

//                                 <Link
//                                   to=''
//                                   replace
//                                   className='d-flex justify-content-center align-align-items-start pt-1 add-btn'
//                                   onClick={() => handleclickTree(tree)}
//                                 >
//                                   <i className='fa-lg fa-fw fs-4 bi bi-plus-square-fill'></i>
//                                 </Link>
//                               </div>
//                             </div>
//                           </Fragment>
//                         ))}
//                       </div>
//                     </PerfectScrollbar>
//                   </div>
//                 </div>
//                 <div className='file-manager-content d-flex flex-column'>
//                   <div className='flex-1 overflow-hidden'>
//                     <PerfectScrollbar className='h-100 p-1 '>
//                       <form
//                         id='extra-info-form'
//                         onSubmit={e => {
//                           e.preventDefault(); // منع إعادة التحميل
//                           // هنا تُطلق دالة الحفظ
//                           handleAddUpdateExtraInfo();
//                         }}
//                       >
//                         <div className='p-3'>
//                           {isAdd && formId && (
//                             <AddExtraRecord
//                               FormId={formId}
//                               ReferenceId={ReferenceId}
//                               setExtraInfoFormData={setExtraInfoFormData} // <-- يرسل الداتا للأب
//                             />
//                           )}

//                           {!isAdd && extraInformationId && (
//                             <UpdateExtraRecord
//                               extraInformationId={extraInformationId}
//                               ReferenceId={ReferenceId}
//                               setExtraInfoFormData={setExtraInfoFormData} // <-- يرسل الداتا للأب
//                             />
//                           )}

//                           {!isAdd && !extraInformationId && (
//                             <div className='d-flex flex-column justify-content-center align-items-center h-100 text-muted pt-5'>
//                               <i className='fa fa-folder-open fa-3x mb-3 opacity-50'></i>
//                               <p className='mb-0 fs-5'>{t('Select Item To View')}</p>
//                               <small>{t('Choose a form or record from the sidebar')}</small>
//                             </div>
//                           )}
//                         </div>
//                       </form>
//                     </PerfectScrollbar>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </CardBody>
//         </Card>
//       </div>

//       <style>
//         {`
//         .tree-wrapper {
//           position: relative;
//         }

//         .tree-wrapper .add-btn {
//           opacity: 0;
//           transition: opacity 0.2s ease-in-out;
//         }

//         .tree-wrapper:hover .add-btn {
//           opacity: 1;
//         }
//       `}
//       </style>
//     </>
//   );
// };

// export default ExtraRecords;
